// Turns an ApiError (or any thrown value) into a message fit for the UI.
// The API's own `detail` text is English and technical, so we only surface it
// where it carries information the user needs (validation and conflicts).
import { t, currentLang, I18N } from './i18n.jsx';

// FastAPI 422 bodies: detail = [{ loc: ['body', 'domain'], msg: '...', type: '...' }, ...]
export function formatValidationDetail(detail) {
  if (!Array.isArray(detail)) return '';
  return detail
    .map((d) => {
      if (!d || typeof d !== 'object') return String(d ?? '');
      const loc = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body' && p !== 'query' && p !== 'path') : [];
      const field = loc.length ? `${loc.join('.')}: ` : '';
      return `${field}${d.msg || ''}`.trim();
    })
    .filter(Boolean)
    .join(' · ');
}

// A readable string from any `detail` shape (string, array, object).
export function detailText(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return formatValidationDetail(detail);
  if (typeof detail === 'object') return detail.message || detail.msg || '';
  return String(detail);
}

/**
 * @param {unknown} err  usually an ApiError
 * @param {object} [opts]
 * @param {string} [opts.lang]
 * @param {'login'|'mfa'} [opts.context]  login screens get their own 401 wording
 * @param {string} [opts.fallback]
 */
export function errorMessage(err, opts = {}) {
  const lang = opts.lang || currentLang();
  const status = err?.status;
  const detail = detailText(err?.payload?.detail);

  if (status === 0) return t(lang, 'err.network');
  if (status === 401) {
    if (opts.context === 'login') return t(lang, 'err.login.401');
    if (opts.context === 'mfa') return t(lang, 'err.login.mfa');
    return t(lang, 'err.401');
  }
  if (status === 403) return t(lang, 'err.403');
  if (status === 404) return t(lang, 'err.404');
  if (status === 429) return t(lang, 'err.429');
  if (status === 422) {
    return detail ? `${t(lang, 'err.422')} ${detail}` : t(lang, 'err.422');
  }
  if (status === 400 || status === 409) {
    if (opts.context === 'mfa') return t(lang, 'err.login.mfa');
    // A coded detail ({code, message, ...params}) has its own translation when we know the code.
    const coded = err?.payload?.detail;
    const key = coded && typeof coded === 'object' && coded.code ? `err.code.${coded.code}` : null;
    if (key && (I18N[lang]?.[key] || I18N.en[key])) return t(lang, key, coded);
    return detail || (status === 409 ? t(lang, 'err.409') : opts.fallback || t(lang, 'err.generic'));
  }
  if (status >= 500) return t(lang, 'err.5xx');
  return opts.fallback || t(lang, 'err.generic');
}
